import React, { type FC } from 'react'
import { Divider, Grid, InputAdornment, SvgIcon } from '@mui/material'
import { Build, GppGood, LockRounded, PersonRounded, Security, Tag, VpnKey, Warning } from '@mui/icons-material'

import { isArray } from '../../../../utils'

import {
    areUserCreationFormsEqual,
    areUserEditingFormsEqual,
    isUserCreatable,
    isUserRemovable,
    isUserUpdatable,
    MFAStatus,
    selectUsersManagementUserCreation,
    selectUsersManagementUserUpdating,
    Translation,
    type UserCreationForm,
    type UserEditingForm,
} from '../../../../domain'

import { Button, Validations, VisibilityToggle } from '../../common'
import { CustomCopy } from '../../Copy'
import { FormattedTranslation, useTranslator } from '../../Internationalization'
import { EditableWarning, EntryInput, EntryInputLabel, GridFormEntry, GridFormItem, ManagedDialog, type ManagedDialogContentProps } from '../Modal'
import { PermissionsTable, PermissionsTemplateSection } from '../Permissions'
import { RolesDropdown } from '../Roles'
import { AuthenticationMethodsForm, MqttPublishPrefixInput, MqttPublishPrefixInputValidation } from '.'

const UserModalContent = <Form extends UserEditingForm | UserCreationForm>({
    advanced,
    loading,
    equals,
    form,
    usecase,
}: ManagedDialogContentProps<Form, 'createUserUsecase' | 'editUserUsecase'>): ReturnType<FC> => {
    const id = 'id' in form ? form.id : null
    const translator = useTranslator()

    const disabledUserNameField = !form.canEditName || loading
    const disabledOtherField = !form.canEditOthers || loading
    const disabledMFAField = !form.canUpdateMFA || loading

    return (
        <Grid container spacing={2} alignContent="center" alignItems="center">
            <EditableWarning canEditAll={form.canEditOthers} canUpdateMfa={form.canUpdateMFA} type="user" />
            <GridFormEntry
                label={<EntryInputLabel icon={PersonRounded} label={Translation.USERNAME} />}
                endAdornmentSize={1}
                endAdornment={id && (
                    <CustomCopy button={(props) => <Button data-testid="username-copy-button" variant="legacyAmber" fullWidth {...props} />}>
                        {form.username}
                    </CustomCopy>
                )}
            >
                <EntryInput
                    data-testid="username"
                    placeholder={translator.formatTranslation(Translation.USERNAME)}
                    value={form.username}
                    autoComplete="off"
                    disabled={disabledUserNameField}
                    onChange={({ target }) => usecase.update({ username: target.value.trim() })}
                />
            </GridFormEntry>

            {form.usernameValidation !== null && (
                <GridFormEntry>
                    <Validations
                        validations={isArray(form.usernameValidation) ? form.usernameValidation : [form.usernameValidation]}
                        data-testid="username-validation"
                    />
                </GridFormEntry>
            )}
            <GridFormEntry label={<EntryInputLabel icon={LockRounded} label={Translation.PASSWORD} />}>
                {form.password === null && (
                    <Button
                        data-testid="change-password-button"
                        variant="legacyAmber"
                        disabled={disabledOtherField}
                        onClick={() => usecase.update({ password: '', passwordConfirmation: '' })}
                    >
                        <FormattedTranslation id={Translation.CHANGE_PASSWORD} />
                    </Button>
                )}
                {form.password !== null && (
                    <EntryInput
                        data-testid="password"
                        type={form.passwordConfirmation === null ? 'text' : 'password'}
                        placeholder={translator.formatTranslation(Translation.PASSWORD)}
                        value={form.password}
                        autoComplete="new-password"
                        disabled={disabledOtherField}
                        onChange={({ target }) => usecase.update({ password: target.value })}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <VisibilityToggle
                                            data-testid={form.passwordConfirmation === null ? 'toggle-password-visible' : 'toggle-password-hidden'}
                                            visible={form.passwordConfirmation === null}
                                            disabled={disabledOtherField}
                                            onToggle={() => usecase.togglePasswordMode()}
                                            showLabel={translator.formatTranslation(Translation.SHOW_PASSWORD)}
                                            hideLabel={translator.formatTranslation(Translation.HIDE_PASSWORD)}
                                        />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                )}
            </GridFormEntry>
            <GridFormEntry expand={form.passwordConfirmation !== null} label={<EntryInputLabel icon={SvgIcon} label={Translation.CONFIRM_PASSWORD} />}>
                <EntryInput
                    data-testid="password-confirmation"
                    type="password"
                    value={form.passwordConfirmation || ''}
                    disabled={disabledOtherField}
                    placeholder={translator.formatTranslation(Translation.CONFIRM_PASSWORD)}
                    onChange={({ target }) => usecase.update({ passwordConfirmation: target.value })}
                    autoComplete="new-password"
                />
            </GridFormEntry>
            <GridFormEntry expand={form.password !== null}>
                <Validations
                    validations={form.passwordValidation === null ? [] : isArray(form.passwordValidation) ? form.passwordValidation : [form.passwordValidation]}
                    data-testid="password-validation"
                />
            </GridFormEntry>

            {'mfaStatus' in form && form.mfaStatus !== MFAStatus.NOT_SETUP && (
                <GridFormEntry label={<EntryInputLabel icon={GppGood} label={Translation.MULTI_FACTOR_AUTHENTICATION_ACRONYM} />}>
                    <Button
                        data-testid="disable-mfa-button"
                        variant="legacyAmber"
                        disabled={disabledMFAField || form.mfaStatus === MFAStatus.DISABLED}
                        onClick={() =>
                            usecase.update({ mfaStatus: MFAStatus.DISABLED })}
                    >
                        <FormattedTranslation id={form.mfaStatus === MFAStatus.ENABLED ? Translation.DISABLE : Translation.DISABLED} />
                    </Button>
                </GridFormEntry>
            )}

            {form.isMfaEnforced !== null && (
                <GridFormEntry label={<EntryInputLabel icon={Warning} label={Translation.ENFORCE_MFA_USAGE} />}>
                    <Button
                        data-testid="enforce-usage-button"
                        variant="legacyAmber"
                        disabled={disabledMFAField}
                        onClick={() => usecase.update({ isMfaEnforced: !form.isMfaEnforced })}
                    >
                        <FormattedTranslation id={form.isMfaEnforced ? Translation.DISABLE : Translation.ENABLE} />
                    </Button>
                </GridFormEntry>
            )}

            <GridFormEntry label={<EntryInputLabel icon={Security} label={Translation.ROLE} values={{ count: 2 }} />}>
                <RolesDropdown disabled={disabledOtherField} roles={form.roles} allRoles={form.allRoles} usecase={usecase} />
            </GridFormEntry>

            <GridFormItem expand={advanced} size={12}>
                <Divider />
            </GridFormItem>

            <GridFormEntry expand={advanced} label={<EntryInputLabel icon={Build} label={Translation.ADDITIONAL_PERMISSION} values={{ count: 0 }} />} />
            <PermissionsTemplateSection
                expand={advanced}
                disabled={disabledOtherField}
                usecase={usecase}
                templateInput={form.templateInput}
                templates={form.templates}
                selectedTemplate={form.selectedTemplate}
            />
            <GridFormItem expand={advanced} size={12}>
                <PermissionsTable
                    disabled={disabledOtherField}
                    canLink={equals}
                    permissions={form.permissions}
                    inherited={form.rolesPermissions}
                    usecase={usecase}
                />
            </GridFormItem>
            <GridFormEntry labelSize={4} expand={advanced} label={<EntryInputLabel icon={VpnKey} label={Translation.AUTHENTICATION_METHODS} />}>
                <AuthenticationMethodsForm authenticationMethods={form.authenticationMethods} disabled={disabledOtherField} usecase={usecase} />
            </GridFormEntry>
            <GridFormEntry expand={advanced} label={<EntryInputLabel icon={Tag} label={Translation.MQTT_PUBLISH_PREFIX} />}>
                <MqttPublishPrefixInput mqttPublishPrefix={form.mqttPublishPrefix} disabled={disabledOtherField} usecase={usecase} />
                <MqttPublishPrefixInputValidation mqttPublishPrefixValidation={form.mqttPublishPrefixValidation} />
            </GridFormEntry>
        </Grid>
    )
}

export const UserEditModal: FC = () => (
    <ManagedDialog
        maxWidth="md"
        title={Translation.EDIT_USER}
        deleteConfirmation={Translation.DELETE_USER_CONFIRMATION}
        usecase="editUserUsecase"
        formSelector={selectUsersManagementUserUpdating}
        isRemovable={isUserRemovable}
        areEquals={areUserEditingFormsEqual}
        isConfirmable={isUserUpdatable}
        advanced={false}
    >
        {UserModalContent}
    </ManagedDialog>
)

export const UserCreationModal: FC = () => (
    <ManagedDialog
        maxWidth="md"
        title={Translation.CREATE_USER}
        usecase="createUserUsecase"
        formSelector={selectUsersManagementUserCreation}
        areEquals={areUserCreationFormsEqual}
        isConfirmable={isUserCreatable}
        advanced={false}
    >
        {UserModalContent}
    </ManagedDialog>
)
